/**
 * useInlineEdit — inline cell edit state machine for App-mode lists.
 * Optimistic patchRow → runtimeApi.update; on success re-patches with the resolved
 * server row (refreshes computed/ref _display); on error rolls back and reports.
 *
 * `runtimeApi.update` resolves to the record itself (the PUT endpoint returns
 * `update_record`'s result directly — no `{ data }` envelope; see `ApiService.put`
 * in `@/utils/api`, which returns `response.data` = the raw JSON body). `update_record`
 * ends with `return await self.get_record(...)`, which resolves refs (`_resolve_refs`)
 * and includes every column — so re-patching with the response wholesale is correct.
 */
import { useCallback, useRef, useState } from 'react'
import { runtimeApi } from '@/app/services/runtimeApi'

interface EditingCell { rowId: string; field: string }

export function useInlineEdit(opts: {
  connectionId: string
  objectName: string
  patchRow: (id: string, updater: (r: Record<string, unknown>) => Record<string, unknown>) => void
  onError?: (msg: string) => void
}) {
  const { connectionId, objectName, patchRow, onError } = opts
  const [editing, setEditing] = useState<EditingCell | null>(null)
  const [draft, setDraft] = useState<unknown>(null)
  const [saving, setSaving] = useState(false)
  const prev = useRef<unknown>(null)

  const begin = useCallback((rowId: string, field: string, current: unknown) => {
    prev.current = current; setDraft(current); setEditing({ rowId, field })
  }, [])

  const cancel = useCallback(() => { setEditing(null); setDraft(null) }, [])

  const commit = useCallback(async () => {
    if (!editing) return
    const { rowId, field } = editing
    const value = draft
    const before = prev.current
    if (value === before) { setEditing(null); return }
    setEditing(null); setSaving(true)
    patchRow(rowId, (r) => ({ ...r, [field]: value }))
    try {
      const updated = await runtimeApi.update(connectionId, objectName, rowId, { [field]: value })
      if (updated && typeof updated === 'object') patchRow(rowId, () => updated)
    } catch (e) {
      patchRow(rowId, (r) => ({ ...r, [field]: before }))
      onError?.(e instanceof Error ? e.message : 'Failed to update')
    } finally {
      setSaving(false)
    }
  }, [editing, draft, connectionId, objectName, patchRow, onError])

  return { editing, draft, saving, begin, setDraft, cancel, commit }
}
